import {
  Card,
} from "../../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTheme } from "next-themes";
import { useEffect, useState, useMemo } from "react";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { useChartColors } from "../../../hooks/useChartColors";
import AccountingLoader from "../../../components/loaders/AccountingLoader";
import { useAccountingEcomm } from "../hooks/useAccountingEcomm";
import { getMinMaxRevenue } from "../../../utils/charts";
import { formatCurrency } from "../../../utils/formatCurrency";

export function EcommRevenueOverview() {

  const { theme } = useTheme();
  const [, setMounted] = useState(false);
  const [tab, setTab] = useState<"month" | "quarter" | "year" | "all_time">("month");

  const { data, isLoading } = useAccountingEcomm(tab);

  const revenueData = data?.revenue_overview;

  useEffect(() => {
    setMounted(true);
  }, []);

  const HorizontalHoverLine = ({ points, viewBox }: any) => {
    if (!points?.[0] || !viewBox) return null;

    const { y } = points[0]; // Y position of hovered point
    const { x, width } = viewBox; // Full chart width

    return (
      <line
        x1={x}
        x2={x + width}
        y1={y}
        y2={y}
        stroke="var(--chart-axis)"
        strokeWidth={1}
        strokeDasharray="3 3"
      />
    );
  };

  const monthlyChartData = useMemo(() => {
    if (!revenueData || tab !== "month") return [];
    return revenueData.map((item: { period: any; total_revenue: number; }) => ({
      month: item.period,
      revenue: item.total_revenue,
    }));
  }, [revenueData, tab]);

  const quarterlyChartData = useMemo(() => {
    if (!revenueData || tab !== "quarter") return [];
    return revenueData.map((item: { period: any; total_revenue: number; }) => ({
      quarter: item.period,
      revenue: item.total_revenue,
    }));
  }, [revenueData, tab]);

  const yearlyChartData = useMemo(() => {
    if (!revenueData || tab !== "year") return [];
    return revenueData.map((item: { period: any; total_revenue: number; }) => ({
      year: item.period,
      revenue: item.total_revenue,
    }));
  }, [revenueData, tab]);

  const allTimeChartData = useMemo(() => {
    if (!revenueData || tab !== "all_time") return [];
    return revenueData.map((item: { period: any; total_revenue: number; }) => ({
      label: item.period,
      revenue: item.total_revenue,
    }));
  }, [revenueData, tab]);

  const monthlyRange = useMemo(() => getMinMaxRevenue(monthlyChartData), [monthlyChartData]);
  const quarterlyRange = useMemo(() => getMinMaxRevenue(quarterlyChartData), [quarterlyChartData]);
  const yearlyRange = useMemo(() => getMinMaxRevenue(yearlyChartData), [yearlyChartData]);
  const allTimeRange = useMemo(() => getMinMaxRevenue(allTimeChartData), [allTimeChartData]);

  const smartFormatter = (value: number) => {
    if (Math.abs(value) >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(1)}M`; // → $1.2M
    }
    if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(0)}k`; // → $8k
    }
    return `$${value}`; // For small values
  };

 



  const chartColors = useMemo(
    () => ({
      grid:
        theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
      text:
        theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)",
      tooltip: {
        bg:
          theme === "dark" ? "rgba(0, 0, 0, 0.8)" : "rgba(255, 255, 255, 0.8)",
        border:
          theme === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
        text: theme === "dark" ? "#ffffff" : "#000000",
      },
    }),
    [theme]
  );

  const colors = useChartColors("ecommerce");
  if (!colors) return null;

  // For static labels below the chart
  interface YearlyChartItem {
    year: string;
    revenue: number;
  }


  return (
    <>
      <SummaryHeader
        title="Revenue Overview"
      />
      <Card>
        <Tabs value={tab} onValueChange={(val) => setTab(val as typeof tab)}>
          <TabsList className="flex flex-wrap gap-1">
            <TabsTrigger value="month">Monthly</TabsTrigger>
            <TabsTrigger value="quarter">Quarterly</TabsTrigger>
            <TabsTrigger value="year">Yearly</TabsTrigger>
            <TabsTrigger value="all_time">All Time</TabsTrigger>
          </TabsList>

          <TabsContent value="month">
            {isLoading ? (
              <AccountingLoader variant="revenue-ovr" itemCount={1} />
            ) : (
              <ResponsiveContainer key={tab} width="100%" height={420}>
                <AreaChart data={monthlyChartData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor={colors[0]}
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor={colors[0]}
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--chart-grid)"
                  />
                  <XAxis dataKey="month" stroke="var(--chart-axis)" tick={{ fill: "var(--chart-text)", fontSize: 12.5 }} />
                  <YAxis
                    stroke="var(--chart-axis)"
                    domain={([dataMin, dataMax]: [number, number]) => [
                      Math.min(0, dataMin),
                      Math.max(0, dataMax)
                    ] as [number, number]}
                    tickFormatter={smartFormatter}
                    ticks={
                      (() => {
                        const min = Math.min(0, monthlyRange.min);
                        const max = Math.max(0, monthlyRange.max);
                        const step = Math.ceil((max - min) / 5 / 1000) * 1000 || 1000;
                        const ticks = [];
                        for (let i = min; i <= max; i += step) {
                          ticks.push(i);
                        }
                        if (!ticks.includes(0)) {
                          ticks.push(0);
                          ticks.sort((a, b) => a - b);
                        }
                        return ticks;
                      })()
                    }
                  /* interval={3} */
                  />
                  <ReferenceLine
                    y={0}
                    stroke="var(--foreground)" // customize this color
                  />


                  <Tooltip
                    formatter={(value) => [
                      formatCurrency(value as number),
                      "Revenue",
                    ]}
                    contentStyle={{
                      backgroundColor: "var(--background)",
                      borderColor: "var(--border)",
                      borderRadius: "var(--radius)",
                      fontSize: "0.875rem",
                    }}
                    itemStyle={{
                      color: "var(--chart-text)",
                    }}
                    cursor={<HorizontalHoverLine />}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke={colors[0]}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                    strokeWidth="2"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </TabsContent>


          <TabsContent value="quarter">
            {isLoading ? (
              <AccountingLoader variant="revenue-ovr" itemCount={1} />
            ) : (
              <ResponsiveContainer width="100%" height={420}>
                <BarChart
                  data={revenueData.map((item: { period: string; total_revenue: number; }) => {
                    // Transform "Q1_2023" → "Q1 2023"
                    const [quarter, year] = item.period.split("_");
                    return {
                      quarter: `${quarter} ${year}`,
                      revenue: item.total_revenue,
                    };
                  })}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={chartColors.grid}
                  />
                  <XAxis dataKey="quarter" stroke="var(--chart-axis)" />
                  <YAxis
                    stroke="var(--chart-axis)"
                    tickFormatter={smartFormatter}
                    domain={[0, quarterlyRange.max]}
                  />
                  <Tooltip
                    formatter={(value) => [
                      formatCurrency(value as number),
                      "Revenue",
                    ]}
                    labelFormatter={(label) => label} 
                    contentStyle={{
                      backgroundColor: chartColors.tooltip.bg,
                      border: `1px solid ${chartColors.tooltip.border}`,
                      borderRadius: "4px",
                      color: chartColors.tooltip.text,
                    }}
                    labelStyle={{ color: chartColors.tooltip.text }}
                  />
                  <Bar
                    dataKey="revenue"
                    fill={colors[1]}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </TabsContent>

          <TabsContent value="year">
            {isLoading ? (
              <AccountingLoader variant="revenue-ovr" itemCount={1} />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart data={yearlyChartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={chartColors.grid}
                    />
                    <XAxis dataKey="year" stroke="var(--chart-axis)" />
                    <YAxis
                      stroke="var(--chart-axis)"
                      tickFormatter={smartFormatter}
                      domain={[0, yearlyRange.max]}
                    />
                    <Tooltip
                      formatter={(value) => [
                        formatCurrency(value as number),
                        "Revenue",
                      ]}
                      contentStyle={{
                        backgroundColor: chartColors.tooltip.bg,
                        border: `1px solid ${chartColors.tooltip.border}`,
                        borderRadius: "4px",
                        color: chartColors.tooltip.text,
                      }}
                      labelStyle={{ color: chartColors.tooltip.text }}
                    />
                    <Bar
                      dataKey="revenue"
                      fill={colors[3]}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-4 text-center text-base font-semibold space-y-1">
                  {yearlyChartData.map((item: YearlyChartItem) => (
                    <div key={item.year}>
                      Revenue({item.year}): {formatCurrency(item.revenue)}
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>
          <TabsContent value="all_time">
            {isLoading ? (
              <AccountingLoader variant="revenue-ovr" itemCount={1} />
            ) : revenueData.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={revenueData.map((item: { period: string; total_revenue: number; }) => ({
                    year: item.period,
                    revenue: item.total_revenue,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  {/* Static X axis label */}
                  <XAxis
                    dataKey="label"
                    stroke="var(--chart-axis)"
                    label={{
                      value: "All time",                
                    }}
                  />
                  <YAxis
                    stroke="var(--chart-axis)"
                    tickFormatter={smartFormatter}
                    domain={[Math.min(0, allTimeRange.min), allTimeRange.max]} />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value as number), "Revenue"]}
                    contentStyle={{
                      backgroundColor: chartColors.tooltip.bg,
                      border: `1px solid ${chartColors.tooltip.border}`,
                      borderRadius: "4px",
                      color: chartColors.tooltip.text,
                    }}
                    labelStyle={{ color: chartColors.tooltip.text }}
                  />
                  <Bar dataKey="revenue" fill={colors[6] ?? colors[0]} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </TabsContent>
        </Tabs>
      </Card>
    </>
  );
}
